import { useAtomValue } from 'jotai'
import React, { type CSSProperties, ReactNode, useCallback, useMemo } from 'react'

import svgOrdersSrc from '@cowprotocol/assets/svg/orders.svg'
import { useMediaQuery, useTheme, useThrottledCallback } from '@cowprotocol/common-hooks'
import { isInjectedWidget, isSellOrder, maxAmountSpend } from '@cowprotocol/common-utils'
import { SupportedChainId } from '@cowprotocol/cow-sdk'
import { Currency } from '@cowprotocol/currency'
import { ButtonOutlined, Media } from '@cowprotocol/ui'
import { isSafeWalletAtom, isSmartContractWalletAtom, useWalletDetails, useWalletInfo } from '@cowprotocol/wallet'

import { Trans, useLingui } from '@lingui/react/macro'
import { useInjectedWidgetParams } from 'entities/injectedWidget'
import SVG from 'react-inlinesvg'
import { Nullish } from 'types'

import { Field } from 'legacy/state/types'

import { useToggleAccountModal } from 'modules/account'
import { CaptchaWidget } from 'modules/captcha'
import { useOpenTokenSelectWidget } from 'modules/tokensList'
import { TradeFormValidation, useGetTradeFormValidation, useSolanaTradeOverhead } from 'modules/tradeFormValidation'
import { WalletStatusButton } from 'modules/wallet'

import { useIsProviderNetworkDeprecated } from 'common/hooks/useIsProviderNetworkDeprecated'
import { useIsProviderNetworkUnsupported } from 'common/hooks/useIsProviderNetworkUnsupported'
import { TradeType, useTradeTypeInfoFromUrl } from 'common/modules/tradeNavigation'
import { CurrencyArrowSeparator } from 'common/pure/CurrencyArrowSeparator'
import { CurrencyInputPanel, CurrencyInputPanelProps } from 'common/pure/CurrencyInputPanel'
import { PoweredFooter } from 'common/pure/PoweredFooter'

import * as styledEl from './styled'
import { mapCurrencyInfo } from './TradeWidgetForm.utils'
import { TradeWidgetProps } from './types'

import { useDerivedTradeState } from '../../hooks/useDerivedTradeState'
import { useIsCurrentTradeBridging } from '../../hooks/useIsCurrentTradeBridging'
import { useIsEoaEthFlow } from '../../hooks/useIsEoaEthFlow'
import { useIsQuoteUpdatePossible } from '../../hooks/useIsQuoteUpdatePossible'
import { useIsWrapOrUnwrap } from '../../hooks/useIsWrapOrUnwrap'
import { useResetReceiverConfirmationOnWalletChange } from '../../hooks/useResetReceiverConfirmationOnWalletChange'
import { useResetRecipientOnChainChange } from '../../hooks/useResetRecipientOnChainChange'
import { useSetOrdersTableDrawerOpen } from '../../hooks/useSetOrdersTableDrawerOpen'
import { useShouldHideQuoteAmounts } from '../../hooks/useShouldHideQuoteAmounts'
import { useSolanaWrapReceiveAmount } from '../../hooks/useSolanaWrapReceiveAmount'
import { useIsWithRecipient } from '../../hooks/useWithRecipient'
import { SetRecipient } from '../../pure/SetRecipient'
import { useIsAlternativeOrderModalVisible } from '../../state/alternativeOrder'
import { useSetNonEvmReceiverConfirmed } from '../../state/nonEvmReceiverConfirmedAtom.atoms'
import { QuotePolingProgress } from '../QuotePolingProgress'
import { TradeWarnings } from '../TradeWarnings'
import { TradeWidgetLinks } from '../TradeWidgetLinks'
import { WrapFlowActionButton } from '../WrapFlowActionButton'

const noop: () => void = () => void 0

// TODO: Break down this large function into smaller functions
// TODO: Reduce function complexity by extracting logic
// eslint-disable-next-line max-lines-per-function, complexity
export function TradeWidgetForm(props: TradeWidgetProps): ReactNode {
  const isInjectedWidgetMode = isInjectedWidget()
  const { standaloneMode, hideOrdersTable, cardStyle, disableSwitchingTokens } = useInjectedWidgetParams()
  const isMobile = useMediaQuery(Media.upToSmall(false))

  const tradeTypeInfo = useTradeTypeInfoFromUrl()
  const isAlternativeOrderModalVisible = useIsAlternativeOrderModalVisible()
  const isLimitOrderTrade = tradeTypeInfo?.tradeType === TradeType.LIMIT_ORDER
  const shouldLockForAlternativeOrder = isAlternativeOrderModalVisible && isLimitOrderTrade
  const isWrapOrUnwrap = useIsWrapOrUnwrap()
  const isCurrentTradeBridging = useIsCurrentTradeBridging()
  const { orderKind } = useDerivedTradeState() || {}
  const { darkMode } = useTheme()

  const isSellTrade = !!orderKind && isSellOrder(orderKind)
  const hideQuoteAmount = useShouldHideQuoteAmounts()

  const { slots, actions, params, disableOutput } = props
  const { settingsWidget, lockScreen, topContent, middleContent, bottomContent, outerContent } = slots

  const { onCurrencySelection, onUserInput, onSwitchTokens, onChangeRecipient } = actions
  const {
    compactView,
    showRecipient,
    isTradePriceUpdating,
    priceImpact,
    recipient,
    hideTradeWarnings,
    enableSmartSlippage,
    displayTokenName = false,
    displayChainName = isCurrentTradeBridging,
    isMarketOrderWidget = false,
    isSellingEthSupported = false,
    isPriceStatic = false,
  } = params

  const inputCurrencyInfo = useMemo(() => {
    const info = isPriceStatic
      ? props.inputCurrencyInfo
      : mapCurrencyInfo(props.inputCurrencyInfo, !isSellTrade, hideQuoteAmount)

    if (isWrapOrUnwrap) {
      return { ...info, receiveAmountInfo: null }
    }

    return info
  }, [isWrapOrUnwrap, props.inputCurrencyInfo, hideQuoteAmount, isSellTrade, isPriceStatic])

  const solanaWrapReceiveAmount = useSolanaWrapReceiveAmount()

  const outputCurrencyInfo = useMemo(() => {
    const info = isPriceStatic
      ? props.outputCurrencyInfo
      : mapCurrencyInfo(props.outputCurrencyInfo, isSellTrade, hideQuoteAmount)

    if (isWrapOrUnwrap) {
      // Wrap/unwrap has no quote to mirror, and on Solana the amount can differ from the input — see
      // `useSolanaWrapReceiveAmount`. Fall back to the input while that's still being previewed.
      return { ...info, amount: solanaWrapReceiveAmount ?? props.inputCurrencyInfo.amount, receiveAmountInfo: null }
    }

    return info
  }, [
    isWrapOrUnwrap,
    props.outputCurrencyInfo,
    props.inputCurrencyInfo.amount,
    solanaWrapReceiveAmount,
    hideQuoteAmount,
    isSellTrade,
    isPriceStatic,
  ])

  const { chainId, account } = useWalletInfo()
  const { allowsOffchainSigning } = useWalletDetails()
  const isProviderNetworkUnsupported = useIsProviderNetworkUnsupported()
  const isProviderNetworkDeprecated = useIsProviderNetworkDeprecated()
  const isSafeWallet = useAtomValue(isSafeWalletAtom)
  const isSmartContractWallet = useAtomValue(isSmartContractWalletAtom)
  const isSmartContractWalletBridging = !!isSmartContractWallet && isCurrentTradeBridging
  const openTokenSelectWidget = useOpenTokenSelectWidget()
  const primaryFormValidation = useGetTradeFormValidation()
  const isEoaEthFlow = useIsEoaEthFlow()
  const isQuoteUpdatePossible = useIsQuoteUpdatePossible()
  const handleNonEvmConfirm = useSetNonEvmReceiverConfirmed()

  useResetReceiverConfirmationOnWalletChange()

  const sellToken = inputCurrencyInfo.currency
  const buyToken = outputCurrencyInfo.currency
  useResetRecipientOnChainChange(buyToken?.chainId, recipient || '', onChangeRecipient)
  const areCurrenciesLoading = !sellToken && !buyToken
  const bothCurrenciesSet = !!sellToken && !!buyToken

  const withRecipient = useIsWithRecipient(showRecipient)
  const solanaTradeOverhead = useSolanaTradeOverhead()
  const maxBalance = maxAmountSpend(
    inputCurrencyInfo.balance || undefined,
    isSafeWallet,
    solanaTradeOverhead?.maxReserve,
  )
  const showSetMax = maxBalance?.greaterThan(0) && !inputCurrencyInfo.amount?.equalTo(maxBalance)

  const disablePriceImpact =
    !!params.disablePriceImpact ||
    primaryFormValidation === TradeFormValidation.QuoteErrors ||
    primaryFormValidation === TradeFormValidation.CurrencyNotSupported ||
    primaryFormValidation === TradeFormValidation.WrapUnwrapFlow

  // Disable too frequent tokens switching
  const throttledOnSwitchTokens = useThrottledCallback(onSwitchTokens, 500)

  const isUpToLarge = useMediaQuery(Media.upToLarge(false))

  const isConnectedMarketOrderWidget = !!account && isMarketOrderWidget

  const shouldShowMyOrdersButton =
    !shouldLockForAlternativeOrder &&
    (!isInjectedWidgetMode && isConnectedMarketOrderWidget ? isUpToLarge : true) &&
    (isConnectedMarketOrderWidget || !hideOrdersTable) &&
    ((isConnectedMarketOrderWidget && standaloneMode !== true && !lockScreen) ||
      (!isMarketOrderWidget && isUpToLarge && !lockScreen))

  const showDropdown = shouldShowMyOrdersButton || isInjectedWidgetMode || isMobile

  const currencyInputCommonProps = {
    isProviderNetworkUnsupported,
    isProviderNetworkDeprecated,
    chainId,
    areCurrenciesLoading,
    bothCurrenciesSet,
    onCurrencySelection,
    onUserInput,
    allowsOffchainSigning,
    tokenSelectorDisabled: shouldLockForAlternativeOrder,
    displayTokenName,
    displayChainName,
    isBridging: isCurrentTradeBridging,
  } as const satisfies Partial<CurrencyInputPanelProps>

  const openSellTokenSelect = useCallback(
    (selectedToken: Nullish<Currency>, field: Field | undefined, onSelectToken: (currency: Currency) => void) => {
      openTokenSelectWidget(selectedToken, field, buyToken || undefined, onSelectToken)
    },
    [openTokenSelectWidget, buyToken],
  )

  const openBuyTokenSelect = useCallback(
    (selectedToken: Nullish<Currency>, field: Field | undefined, onSelectToken: (currency: Currency) => void) => {
      openTokenSelectWidget(selectedToken, field, sellToken || undefined, onSelectToken)
    },
    [openTokenSelectWidget, sellToken],
  )

  const toggleAccountModal = useToggleAccountModal()
  const setOrdersTableDrawerOpen = useSetOrdersTableDrawerOpen()

  const handleMyOrdersClick = useCallback(() => {
    if (isMarketOrderWidget) {
      toggleAccountModal()
    } else {
      setOrdersTableDrawerOpen(true)
    }
  }, [isMarketOrderWidget, setOrdersTableDrawerOpen, toggleAccountModal])

  const isOutputTokenUnsupported = !!buyToken && !(buyToken.chainId in SupportedChainId)

  const { t } = useLingui()

  const disableTokensSwitching =
    shouldLockForAlternativeOrder ||
    isOutputTokenUnsupported ||
    isProviderNetworkUnsupported ||
    isProviderNetworkDeprecated ||
    disableSwitchingTokens === true

  return (
    <>
      <styledEl.ContainerBox id="card" style={isInjectedWidgetMode ? (cardStyle as CSSProperties) : undefined}>
        <styledEl.Header>
          {shouldLockForAlternativeOrder ? <div></div> : <TradeWidgetLinks isDropdown={showDropdown} />}
          {isInjectedWidgetMode && standaloneMode !== false && (
            <WalletStatusButton variant="widget" onWalletClick={toggleAccountModal} />
          )}

          {shouldShowMyOrdersButton && (
            <ButtonOutlined margin={'0 16px 0 auto'} onClick={handleMyOrdersClick}>
              <Trans>
                My orders <SVG src={svgOrdersSrc} />
              </Trans>
            </ButtonOutlined>
          )}

          <styledEl.HeaderRight>
            {!lockScreen && (
              <>
                {!isPriceStatic && !showDropdown && isQuoteUpdatePossible && <QuotePolingProgress />}
                {settingsWidget}
              </>
            )}
          </styledEl.HeaderRight>
        </styledEl.Header>

        {lockScreen ? (
          lockScreen
        ) : (
          <>
            {topContent}
            <div>
              <CurrencyInputPanel
                id="input-currency-input"
                currencyInfo={inputCurrencyInfo}
                showSetMax={showSetMax}
                maxBalance={maxBalance}
                topLabel={isWrapOrUnwrap ? undefined : inputCurrencyInfo.label}
                topContent={inputCurrencyInfo.topContent}
                openTokenSelectWidget={openSellTokenSelect}
                customSelectTokenButton={params.customSelectTokenButton}
                {...currencyInputCommonProps}
              />
            </div>
            {!isWrapOrUnwrap && middleContent}

            <styledEl.CurrencySeparatorBox compactView={compactView}>
              <CurrencyArrowSeparator
                isCollapsed={compactView}
                hasSeparatorLine={!compactView}
                onSwitchTokens={disableTokensSwitching ? noop : throttledOnSwitchTokens}
                isLoading={Boolean(sellToken && outputCurrencyInfo.currency && isTradePriceUpdating)}
                disabled={disableTokensSwitching}
                isDarkMode={darkMode}
              />
            </styledEl.CurrencySeparatorBox>
            <div>
              <CurrencyInputPanel
                id="output-currency-input"
                inputDisabled={
                  (isSellingEthSupported && isEoaEthFlow) || isWrapOrUnwrap || isCurrentTradeBridging || disableOutput
                }
                inputTooltip={
                  isSellingEthSupported && isEoaEthFlow
                    ? t`You cannot edit this field when selling` + ` ${inputCurrencyInfo?.currency?.symbol}`
                    : undefined
                }
                currencyInfo={outputCurrencyInfo}
                priceImpactParams={!disablePriceImpact ? priceImpact : undefined}
                topLabel={isWrapOrUnwrap ? undefined : outputCurrencyInfo.label}
                topContent={outputCurrencyInfo.topContent}
                openTokenSelectWidget={openBuyTokenSelect}
                customSelectTokenButton={params.customSelectTokenButton}
                {...currencyInputCommonProps}
              />
            </div>
            {withRecipient && (
              <SetRecipient
                key={account}
                recipient={recipient || ''}
                onChangeRecipient={onChangeRecipient}
                targetChainId={buyToken?.chainId}
                isBridging={isCurrentTradeBridging}
                isSmartContractWalletBridging={isSmartContractWalletBridging}
                onNonEvmReceiverConfirmedChange={handleNonEvmConfirm}
              />
            )}

            {isWrapOrUnwrap ? (
              sellToken ? (
                <WrapFlowActionButton sellToken={sellToken} />
              ) : null
            ) : (
              bottomContent?.(
                hideTradeWarnings ? null : (
                  <TradeWarnings
                    enableSmartSlippage={enableSmartSlippage}
                    isTradePriceUpdating={isTradePriceUpdating}
                  />
                ),
                <CaptchaWidget />,
              )
            )}
          </>
        )}

        {isInjectedWidgetMode && <PoweredFooter />}
      </styledEl.ContainerBox>
      {outerContent && <styledEl.OuterContentWrapper>{outerContent}</styledEl.OuterContentWrapper>}
    </>
  )
}
